import api from '../lib/axios';
import type { ApiResponse } from '@maios/shared';

export interface ChatChannelItem {
  id: string;
  type: 'PROJECT' | 'USER' | 'DM';
  name: string | null;
  description: string | null;
  projectId: string | null;
  projectKey: string | null;
  projectVisibility: string | null;
  memberCount: number;
  lastMessage: { content: string; senderName: string; createdAt: string } | null;
  unreadCount: number;
  isPinned: boolean;
  isMuted: boolean;
  lastMessageAt: string | null;
  isArchived?: boolean;
  archivedAt?: string | null;
  dmParticipants?: { userId: string; name: string; avatarUrl: string | null }[];
}

export interface ChatChannelDetail {
  id: string;
  type: 'PROJECT' | 'USER' | 'DM';
  name: string | null;
  description: string | null;
  organizationId: string;
  projectId: string | null;
  project: { id: string; name: string; projectKey: string; visibility: string } | null;
  createdBy: string;
  creatorName: string;
  memberCount: number;
  // CHG-133 Step 6: 표시 일관성 — displayColor/nickname/firstName/lastName optional 추가
  members: {
    userId: string;
    name: string;
    avatarUrl: string | null;
    role: string;
    joinedAt: string;
    displayColor?: string | null;
    nickname?: string | null;
    firstName?: string | null;
    lastName?: string | null;
  }[];
  myRole: string | null;
  isPinned: boolean;
  isMuted: boolean;
  isArchived: boolean;
  archivedAt: string | null;
  createdAt: string;
}

export interface ChatMessageItem {
  messageId: string;
  channelId: string;
  content: string;
  mentions: unknown;
  resolvedMentions: unknown[];
  reactions?: Array<{ emoji: string; count: number; users: string[]; reacted: boolean }>;
  attachments?: Array<{ id: string; fileName: string; fileSize: number; mimeType: string; url: string; thumbnailUrl?: string | null }>;
  isEdited?: boolean;
  editedAt?: string | null;
  isDeleted?: boolean;
  createdAt: string;
  sender: { userId: string; name: string; profileImage: string | null };
  // CHG-075-P4: 스레드 정보
  parentMessageId?: string;
  threadCount?: number;
  lastThreadAt?: string | null;
  threadPreview?: Array<{ userId: string; name: string; profileImage: string | null }>;
  // CHG-075-P4: 고정 메시지
  isPinned?: boolean;
  // CHG-123 REQ-V123-013: 메시지로부터 변환된 작업/할 일
  linkedResources?: {
    issues: Array<{ issueId: string; issueKey: string; title: string; statusName: string; statusColor: string; projectKey: string }>;
    todos: Array<{ todoId: string; title: string; completed: boolean }>;
  };
}

export async function getChannels(params?: { type?: string; search?: string; archived?: string }): Promise<{ channels: ChatChannelItem[] }> {
  const query = new URLSearchParams();
  if (params?.type) query.set('type', params.type);
  if (params?.search) query.set('search', params.search);
  if (params?.archived) query.set('archived', params.archived);
  const res = await api.get<ApiResponse<{ channels: ChatChannelItem[] }>>(`/chat/channels?${query}`);
  return res.data.data!;
}

export async function getChannel(channelId: string): Promise<ChatChannelDetail> {
  const res = await api.get<ApiResponse<ChatChannelDetail>>(`/chat/channels/${channelId}`);
  return res.data.data!;
}

export async function updateChannel(channelId: string, data: { name?: string; description?: string; isPinned?: boolean; isMuted?: boolean }): Promise<void> {
  await api.patch(`/chat/channels/${channelId}`, data);
}

export async function leaveChannel(channelId: string): Promise<void> {
  await api.delete(`/chat/channels/${channelId}/members/me`);
}

export async function getMessages(channelId: string, params?: { cursor?: string; limit?: number }): Promise<{ messages: ChatMessageItem[]; hasMore: boolean; nextCursor: string | null }> {
  const query = new URLSearchParams();
  if (params?.cursor) query.set('cursor', params.cursor);
  if (params?.limit) query.set('limit', String(params.limit));
  const res = await api.get<ApiResponse<{ messages: ChatMessageItem[]; hasMore: boolean; nextCursor: string | null }>>(`/chat/channels/${channelId}/messages?${query}`);
  return res.data.data!;
}

export interface MentionData {
  userId: string;
  name: string;
  offset: number;
  length: number;
}

export async function sendMessage(channelId: string, content: string, attachments?: ChatAttachment[], parentMessageId?: string, mentions?: MentionData[]): Promise<ChatMessageItem> {
  const body: Record<string, unknown> = {};
  if (content) body.content = content;
  if (attachments && attachments.length > 0) body.attachments = attachments;
  if (parentMessageId) body.parentMessageId = parentMessageId;
  if (mentions && mentions.length > 0) body.mentions = mentions;
  const res = await api.post<ApiResponse<ChatMessageItem>>(`/chat/channels/${channelId}/messages`, body);
  return res.data.data!;
}

export async function markAsRead(channelId: string): Promise<void> {
  await api.post(`/chat/channels/${channelId}/read`);
}

// ── Thread ──

export interface ThreadResponse {
  parentMessage: ChatMessageItem & { threadCount: number; lastThreadAt: string | null };
  messages: ChatMessageItem[];
  nextCursor: string | null;
}

export async function getThread(channelId: string, messageId: string, cursor?: string): Promise<ThreadResponse> {
  const query = new URLSearchParams();
  if (cursor) query.set('cursor', cursor);
  const res = await api.get<ApiResponse<ThreadResponse>>(`/chat/channels/${channelId}/messages/${messageId}/thread?${query}`);
  return res.data.data!;
}

// ── Archive ──

export async function archiveChannel(channelId: string): Promise<void> {
  await api.post(`/chat/channels/${channelId}/archive`);
}

export async function unarchiveChannel(channelId: string): Promise<void> {
  await api.post(`/chat/channels/${channelId}/unarchive`);
}

// ── Pinned Messages ──

export interface PinnedMessageItem {
  id: string;
  messageId: string;
  message: {
    content: string;
    sender: { userId: string; name: string; profileImage: string | null };
    createdAt: string;
    attachments: unknown[];
  };
  pinnedBy: { userId: string; name: string };
  pinnedAt: string;
}

export interface PinnedMessagesResponse {
  pins: PinnedMessageItem[];
  pagination: { page: number; limit: number; total: number };
}

export async function getPinnedMessages(channelId: string, page?: number, limit?: number): Promise<PinnedMessagesResponse> {
  const query = new URLSearchParams();
  if (page) query.set('page', String(page));
  if (limit) query.set('limit', String(limit));
  const res = await api.get<ApiResponse<PinnedMessagesResponse>>(`/chat/channels/${channelId}/pins?${query}`);
  return res.data.data!;
}

export async function pinMessage(channelId: string, messageId: string): Promise<{ id: string; messageId: string; pinnedBy: string; pinnedAt: string }> {
  const res = await api.post<ApiResponse<{ id: string; messageId: string; pinnedBy: string; pinnedAt: string }>>(`/chat/channels/${channelId}/pins`, { messageId });
  return res.data.data!;
}

export async function unpinMessage(channelId: string, messageId: string): Promise<void> {
  await api.delete(`/chat/channels/${channelId}/pins/${messageId}`);
}

// ── Phase 2: Channel management ──

export async function createChannel(data: { type: 'USER' | 'DM'; name?: string; description?: string; memberIds: string[] }): Promise<{ id: string; type: string; name: string | null }> {
  const res = await api.post<ApiResponse<{ id: string; type: string; name: string | null }>>('/chat/channels', data);
  return res.data.data!;
}

export async function createDM(memberIds: string[]): Promise<{ id: string; type: string; name: string | null; isExisting: boolean }> {
  const res = await api.post<ApiResponse<{ id: string; type: string; name: string | null; isExisting: boolean }>>('/chat/channels/dm', { memberIds });
  return res.data.data!;
}

export async function inviteMembers(channelId: string, userIds: string[]): Promise<{ added: string[]; alreadyMember: string[] }> {
  const res = await api.post<ApiResponse<{ added: string[]; alreadyMember: string[] }>>(`/chat/channels/${channelId}/members`, { userIds });
  return res.data.data!;
}

export async function removeMember(channelId: string, userId: string): Promise<void> {
  await api.delete(`/chat/channels/${channelId}/members/${userId}`);
}

export async function deleteChannel(channelId: string): Promise<void> {
  await api.delete(`/chat/channels/${channelId}`);
}

export async function kickMember(channelId: string, userId: string): Promise<{ kickedUserId: string; kickedUserName: string }> {
  const res = await api.post<ApiResponse<{ kickedUserId: string; kickedUserName: string }>>(`/chat/channels/${channelId}/members/${userId}/kick`);
  return res.data.data!;
}

// ── Edit / Delete ──

export async function editMessage(channelId: string, messageId: string, content: string): Promise<ChatMessageItem> {
  const res = await api.patch<ApiResponse<ChatMessageItem>>(`/chat/channels/${channelId}/messages/${messageId}`, { content });
  return res.data.data!;
}

export async function deleteMessage(channelId: string, messageId: string): Promise<void> {
  await api.delete(`/chat/channels/${channelId}/messages/${messageId}`);
}

// ── File upload ──

export interface ChatAttachment {
  id: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
  url: string;
  thumbnailUrl: string | null;
}

export async function uploadFile(channelId: string, file: File): Promise<ChatAttachment> {
  const formData = new FormData();
  formData.append('file', file);
  const res = await api.post<ApiResponse<ChatAttachment>>(`/chat/channels/${channelId}/upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return res.data.data!;
}

// ── Reactions ──

export interface ReactionSummary {
  emoji: string;
  count: number;
  users: string[];
  reacted: boolean;
}

export async function toggleReaction(channelId: string, messageId: string, emoji: string): Promise<{ action: 'added' | 'removed'; reactions: ReactionSummary[] }> {
  const res = await api.post<ApiResponse<{ action: 'added' | 'removed'; reactions: ReactionSummary[] }>>(`/chat/channels/${channelId}/messages/${messageId}/reactions`, { emoji });
  return res.data.data!;
}

// ── Search ──

export interface SearchResult {
  messageId: string;
  channelId?: string;
  channelName?: string;
  channelType?: string;
  content: string;
  highlightedContent: string;
  sender: { userId: string; name: string; profileImage: string | null };
  createdAt: string;
}

export interface SearchResponse {
  results: SearchResult[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
}

export async function searchChannelMessages(channelId: string, params: { q: string; page?: number; limit?: number; mentions?: string }): Promise<SearchResponse> {
  const query = new URLSearchParams({ q: params.q });
  if (params.page) query.set('page', String(params.page));
  if (params.limit) query.set('limit', String(params.limit));
  if (params.mentions) query.set('mentions', params.mentions);
  const res = await api.get<ApiResponse<SearchResponse>>(`/chat/channels/${channelId}/messages/search?${query}`);
  return res.data.data!;
}

export async function searchAllMessages(params: { q: string; type?: string; page?: number; limit?: number }): Promise<SearchResponse> {
  const query = new URLSearchParams({ q: params.q });
  if (params.type) query.set('type', params.type);
  if (params.page) query.set('page', String(params.page));
  if (params.limit) query.set('limit', String(params.limit));
  const res = await api.get<ApiResponse<SearchResponse>>(`/chat/messages/search?${query}`);
  return res.data.data!;
}

export interface OrgMember {
  userId: string;
  name: string;
  email: string;
  avatarUrl: string | null;
  // CHG-133 Step 6: 표시 일관성
  displayColor?: string | null;
  nickname?: string | null;
  firstName?: string | null;
  lastName?: string | null;
}

export async function searchOrgMembers(orgId: string): Promise<OrgMember[]> {
  type ServerUser = {
    id: string;
    name: string;
    email: string;
    avatarUrl: string | null;
    displayColor?: string | null;
    nickname?: string | null;
    firstName?: string | null;
    lastName?: string | null;
  };
  const res = await api.get<ApiResponse<{ userId: string; user: ServerUser }[]>>(`/organizations/${orgId}/members`);
  return (res.data.data ?? []).map((m) => ({
    userId: m.user.id,
    name: m.user.name,
    email: m.user.email,
    avatarUrl: m.user.avatarUrl,
    displayColor: m.user.displayColor ?? null,
    nickname: m.user.nickname ?? null,
    firstName: m.user.firstName ?? null,
    lastName: m.user.lastName ?? null,
  }));
}
